import { Avatar, AvatarGroup, Box, Skeleton, Typography } from '@mui/material'
import { Variant } from '@mui/material/styles/createTypography'
import { C1Tooltip } from 'c1ui/components/C1Tooltip'
import { UserAvatar } from 'components/core/avatar'
import { AvatarSize, getAvatarSizeStyles } from 'components/core/avatar/AvatarSizes'
import { avatarStyles } from 'components/core/avatar/styles'
import { C1DisplayNameSkeleton } from 'components/core/display-name-details'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { MinimalAPIError } from 'components/core/errors/api'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { UserValue } from 'lib/actions/user'
import { SearchUsersRequest, UserRef } from 'pbts/c1/api/user/v1/user'
import { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { userSearchApi } from 'redux_toolkit/users'

export type C1MultiUserDiplayProps = {
    users: Array<UserValue | undefined>
    readOnly?: boolean
    maxWidth?: string,
    padded?: boolean
    margin?: boolean
    maxToShow?: number
    avatarSize?: AvatarSize
    typographyVariant?: Variant
    showTooltip?: boolean
    showUserStatus?: boolean
}

export const C1MultiUserDisplaySkeleton: React.FC = () => (
    <AvatarGroup>
        {[1, 2, 3].map((v) => (
            <Avatar key={v} sx={{ ...avatarStyles.avatar, ...getAvatarSizeStyles('md'), backgroundColor: 'grey.50' }}>
                <Skeleton variant="circular" sx={{ ...getAvatarSizeStyles('md') }} />
            </Avatar>
        ))}
    </AvatarGroup>
)

export const C1MultiUserDisplayWithFetch: React.FC<Omit<C1MultiUserDiplayProps, 'users'> & { userIds: string[] }> = ({
    userIds, ...rest
}) => {
    if (userIds.length === 0) return <C1EmptyIndicator />

    const refs = userIds.map((id) => UserRef.create({ id }))

    const { data, isLoading, isError, error } = userSearchApi.useSearch(SearchUsersRequest.create({ refs }))

    if (isLoading) {
        return userIds.length > 1
            ? <C1MultiUserDisplaySkeleton />
            : <C1DisplayNameSkeleton />
    }

    if ((isError && error) || !data) {
        const err = <FormattedMessage id="component_api_error_users" defaultMessage="Error fetching users" />
        return <MinimalAPIError err={err} />
    }

    return <C1MultiUserDisplay users={data.flat()} {...rest} />
}

export const C1MultiUserDisplay: React.FC<C1MultiUserDiplayProps> = ({
    users,
    readOnly,
    maxWidth,
    margin = false,
    padded = false,
    maxToShow = 4,
    avatarSize = 'md',
    typographyVariant = 'body1',
    showTooltip = false,
    showUserStatus = false,
}) => {
    const group = useMemo(() => (<AvatarGroup
        slotProps={{
            additionalAvatar: {
                sx: {
                    ...avatarStyles.avatar,
                    ...getAvatarSizeStyles(avatarSize),
                    marginLeft: '0px',
                    transform: 'translateX(12px)',
                    background: 'transparent',

                    '&.MuiAvatarGroup-avatar': {
                        borderColor: 'transparent',
                    },
                },
            },
        }}
        max={maxToShow}
        component="div"
        spacing="small"
        tabIndex={0}
        sx={{
            marginLeft: '-2px',
            display: 'inline-flex',
            '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
                borderRadius: 1,
            },
        }}
    >
        {users.map((user, i) => user && <UserAvatar key={`${i}-${user.id}`} size={avatarSize} readOnly user={user} decorative />)}
    </AvatarGroup>), [maxToShow, users, avatarSize])

    if (users.length === 0) return <C1EmptyIndicator />

    if (users.length === 1) {
        return <UserDisplayName
            avatarSize={avatarSize}
            margin={margin}
            padded={padded}
            displayNameVariant={typographyVariant}
            user={users[0]}
            readOnly={readOnly}
            includeDetails={false}
            showUserStatus={showUserStatus}
            showTooltip={showTooltip}
            maxWidth={maxWidth}
        />
    }

    return <C1Tooltip placement="right-end" title={<C1MultiUserDisplayToolTip users={users} showTooltip={showTooltip} readOnly={readOnly} />}>
        {group}
    </C1Tooltip>
}

export type C1MultiUserDiplayToolTipProps = {
    users: Array<UserValue | undefined>
    showTooltip?: boolean
    readOnly?: boolean
}

export const C1MultiUserDisplayToolTip: React.FC<C1MultiUserDiplayToolTipProps> = ({
    users,
    showTooltip = true,
    readOnly = false,
}) => {
    if (!users || users.length === 0) return null

    const visible = users.slice(0, 14)
    const removed = users.slice(14).length

    return (
        <Box margin={1}>
            <Typography mb={2} variant="subtitle1" component="div"><FormattedMessage id="components_multi_user_popup_heading" defaultMessage="Users" /></Typography>
            {visible.map((user) => (
                <Box key={user?.id} marginBottom={2}>
                    <UserDisplayName
                        user={user}
                        includeDetails={false}
                        showTooltip={showTooltip}
                        readOnly={readOnly}
                        margin={false}
                    />
                </Box>
            ))}

            {removed > 0 && <Typography variant="caption" color="info.contrastText">
                <FormattedMessage
                    id="components_multi_user_popup_more"
                    defaultMessage="+ {removed, plural, one {# other} other {# others}} users"
                    values={{ removed }}
                />
            </Typography>
            }
        </Box>
    )
}
